import styled, { stylesToResponsiveCssPropsByKeys } from '@grebban/style-system-react';
import brandingTypography, { type Typography } from 'config/branding/typography';
import { STYLES, THEMES, type ButtonTheme } from 'config/branding/buttons';
import CoreButton from '@grebban/react-core-components/Button';
import { media } from '@fogia/core-web/utils/mediaqueries';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { getLinkUrlFromLinkContent } from '@fogia/core-web/utils/internalRefToSlug';
import type { ButtonLinkComponent } from 'libs/sanity/types/generated';
import React from 'react';

const TextWrapper = styled('span')<{ $isUnderline?: boolean }>`
    width: 100%;

    ${({ $isUnderline }) =>
        $isUnderline &&
        `
        text-decoration: underline;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
    `}
`;

/** Wrapper for icon-only buttons so typography line-height does not add extra vertical space. */
const IconOnlyWrapper = styled('span')`
    display: inline-flex;
    align-items: center;
    line-height: 0;
`;

/** Base must be a DOM element (span) so polymorphic `as` works; with as={as || CoreButton} the root is CoreButton when `as` is unset, otherwise the given element (e.g. span). */
const StyledButton = styled('span')`
    background-color: ${({ buttonTheme }) => buttonTheme.backgroundColor};
    color: ${({ buttonTheme }) => buttonTheme.color};
    border: ${({ buttonTheme }) => buttonTheme.border};
    padding: 0;
    transition: color var(--transition-primary-fast);

    ${media.hover} {
        &:hover {
            background-color: ${({ buttonTheme }) => buttonTheme.hover?.backgroundColor ?? buttonTheme.backgroundColor};
            color: ${({ buttonTheme }) => buttonTheme.hover?.color ?? buttonTheme.color};
        }
    }

    &:active {
        background-color: ${({ buttonTheme }) => buttonTheme.active?.backgroundColor ?? buttonTheme.backgroundColor};
        color: ${({ buttonTheme }) => buttonTheme.active?.color ?? buttonTheme.color};
    }

    &:disabled {
        background-color: ${({ buttonTheme }) => buttonTheme.disabled?.backgroundColor ?? buttonTheme.backgroundColor};
        color: ${({ buttonTheme }) => buttonTheme.disabled?.color ?? buttonTheme.color};
        pointer-events: none;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: none;
        border: ${({ buttonTheme }) => buttonTheme.focusOutlineColor};
    }
`;

export interface ButtonProps extends Record<string, unknown> {
    children: React.ReactNode | typeof React.Children | string;
    disabled?: boolean;
    theme?: ButtonTheme;
    url?: string;
    /** Sanity link object: used to resolve URL (internal ref → slug from reference, external → externalUrl) and target from the Sanity ButtonLinkComponent */
    sanityLink?: ButtonLinkComponent;
    target?: '_self' | '_blank';
    size?: 'small' | 'large';
    typography?: Typography | Array<Typography | '' | null>;
    icon?: React.ComponentType<{ color?: string; size?: string }> | React.ReactElement;
    /** Polymorphic: use as="span" when the parent is the link (avoids nested links); or another component e.g. as={SortButton}. Default: CoreButton. */
    as?: 'span' | React.ElementType;
    /** When true, children are wrapped so typography line-height does not add extra vertical space (for icon-only buttons). */
    iconOnly?: boolean;
}

const Button = ({
    children,
    disabled = false,
    type = 'button',
    theme = 'textButtonUnderline',
    url,
    sanityLink,
    target,
    typography,
    size = 'large',
    icon,
    as = undefined,
    iconOnly = false,
    ...rest
}: ButtonProps) => {
    const siteReferences = useAppSelector((state) => state.application?.references) ?? {};
    const pageReferences = useAppSelector((state) => state.page?.data?.references) ?? {};
    const references = { ...siteReferences, ...pageReferences };
    const resolvedFromSanityLink = sanityLink != null ? getLinkUrlFromLinkContent(sanityLink, references) : undefined;

    const href = resolvedFromSanityLink ?? url;
    const linkTarget = sanityLink?.target ?? target;

    const finalTypography: Typography | Array<Typography | '' | null> =
        typography ?? THEMES[theme].sizes[size].typography;
    const isUnderlined = theme === 'textButtonUnderline';
    const buttonTheme = THEMES[theme];
    const isButtonElement = (!as && !href) || as === 'button';

    return (
        <StyledButton
            as={as || CoreButton}
            type={isButtonElement ? type : undefined}
            style={STYLES}
            disabled={disabled}
            buttonTheme={buttonTheme}
            to={href}
            target={linkTarget}
            {...rest}
        >
            {icon}
            {iconOnly ? (
                <IconOnlyWrapper>{children}</IconOnlyWrapper>
            ) : (
                <TextWrapper
                    $isUnderline={isUnderlined}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- The style package is actually incorrect typed, it should be "string | Array<string | null>" and not only strings.
                    // @ts-ignore
                    {...stylesToResponsiveCssPropsByKeys(finalTypography, brandingTypography)}
                >
                    {children}
                </TextWrapper>
            )}
        </StyledButton>
    );
};

export default Button;
